import React, { useState, useRef, useEffect } from "react";
import { Plus } from "lucide-react";

/**
 * Reusable FAQ accordion.
 * Props:
 *   items: [{ q: string, a: string|ReactNode }]
 *   testIdPrefix: string (default: "faq")
 *   allowMultiple: boolean (default: false)
 */
export default function FAQAccordion({ items, testIdPrefix = "faq", allowMultiple = false }) {
  const [openSet, setOpenSet] = useState(() => new Set());

  const toggle = (i) => {
    setOpenSet((prev) => {
      const next = new Set(allowMultiple ? prev : []);
      if (prev.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <div data-testid={testIdPrefix} className="space-y-2">
      {items.map((it, i) => (
        <FAQItem
          key={`${testIdPrefix}-${i}`}
          index={i}
          q={it.q}
          a={it.a}
          open={openSet.has(i)}
          onToggle={() => toggle(i)}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
}

function FAQItem({ q, a, open, onToggle, index, testIdPrefix }) {
  const ref = useRef(null);
  const [maxH, setMaxH] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    setMaxH(open ? ref.current.scrollHeight : 0);
  }, [open, a]);

  return (
    <div
      data-testid={`${testIdPrefix}-item-${index}`}
      data-open={open ? "true" : "false"}
      className={`relative cc-surface rounded-xl overflow-hidden transition-colors duration-200 border ${
        open ? "border-[#00ff88]/30" : "border-[#15152a] hover:border-[#1f1f3a]"
      }`}
    >
      {/* Green accent left border when active */}
      <span
        aria-hidden
        className={`absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-300 ${
          open ? "bg-[#00ff88] shadow-[0_0_12px_rgba(0,255,136,0.45)]" : "bg-transparent"
        }`}
      />
      <button
        type="button"
        data-testid={`${testIdPrefix}-q-${index}`}
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 group"
      >
        <span
          className={`text-sm md:text-base transition-colors ${
            open ? "text-white font-medium" : "text-[#d0d0e0] group-hover:text-white"
          }`}
        >
          {q}
        </span>
        <span
          aria-hidden
          className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center transition-all duration-300 ${
            open
              ? "border-[#00ff88]/50 bg-[#00ff88]/10 text-[#00ff88] rotate-45"
              : "border-[#15152a] text-[#00ff88] rotate-0 group-hover:border-[#00ff88]/30"
          }`}
        >
          <Plus size={14} />
        </span>
      </button>

      <div
        data-testid={`${testIdPrefix}-a-${index}`}
        style={{ maxHeight: `${maxH}px` }}
        className="overflow-hidden transition-[max-height] duration-300 ease-out"
        aria-hidden={!open}
      >
        <div ref={ref} className="px-5 pb-5 pt-0 text-sm text-[#a0a0b0] leading-relaxed">
          {a}
        </div>
      </div>
    </div>
  );
}
